// Pure decision logic for blueprint co-editing sync and echo-loop prevention.
// No React, no component imports — suitable for unit testing without oxc parser issues.

/**
 * computeRemoteApplyState — extract state updates from an incoming remote blueprint.
 *
 * Given an incoming blueprint from the collab doc, decide which graph sections
 * (body, face, events) to apply and extract their state. Returns an object
 * describing what should be applied; the caller is responsible for actually
 * applying state mutations.
 *
 * @param {Object} incomingBlueprint - Blueprint from remote source
 * @returns {Object} { bodyState, faceState, eventState, variables }
 *   - bodyState: { nodes, edges } or null if not present in blueprint
 *   - faceState: { nodes, edges } or null if not present in blueprint
 *   - eventState: { nodes, edges, functions } or null if not present in blueprint
 *   - variables: array or null if not present in blueprint
 */
export function computeRemoteApplyState(incomingBlueprint) {
  const updates = {
    bodyState: null,
    faceState: null,
    eventState: null,
    variables: null
  }

  // Apply body graph
  if (incomingBlueprint.body?.nodes !== undefined) {
    updates.bodyState = {
      nodes: incomingBlueprint.body.nodes ?? [],
      edges: incomingBlueprint.body.edges ?? []
    }
  }

  // Apply face graph
  if (incomingBlueprint.face?.nodes !== undefined) {
    updates.faceState = {
      nodes: incomingBlueprint.face.nodes ?? [],
      edges: incomingBlueprint.face.edges ?? []
    }
  }

  // Apply event graph
  if (incomingBlueprint.events?.eventNodes !== undefined) {
    updates.eventState = {
      nodes: incomingBlueprint.events.eventNodes ?? [],
      edges: incomingBlueprint.events.eventEdges ?? [],
      functions: incomingBlueprint.events.functions ?? {}
    }
    // Sync variables if present
    if (incomingBlueprint.events.variables?.length) {
      updates.variables = incomingBlueprint.events.variables
    }
  }

  return updates
}

/**
 * shouldRouteEditToDoc — decide whether a local edit should be routed to the collab doc.
 *
 * This is the echo-loop guard: when the editor applies a remote blueprint change,
 * it sets isApplyingRemote=true to block any auto-save effects from routing the
 * applied state back to the doc as a new edit. Also routes local edits only when
 * the collab client is synced and the feature flag is enabled.
 *
 * @param {boolean} isCollabEnabled - Feature flag isBlueprintCollabCoeditEnabled()
 * @param {boolean} isCollabActive - Collab client is connected AND synced (isBlueprintCollabActive())
 * @param {boolean} isApplyingRemote - True during remote blueprint application
 * @returns {boolean} true if local edit should be routed to writeBlueprintCollab()
 */
export function shouldRouteEditToDoc(isCollabEnabled, isCollabActive, isApplyingRemote) {
  // Echo-loop guard: if we're currently applying a remote update, don't route
  // the state change back to the doc as a new local edit
  if (isApplyingRemote) return false

  // Route only if collab is enabled AND the client is actively synced
  return isCollabEnabled && isCollabActive
}
